import React, { useEffect, useMemo, useState } from 'react';
import type { AgenticContext } from 'agentic_chat/types';
import { doesClipWorkOnAlignedStreams } from '@goldcast/player-config/src/utils/clipUtils';
import { AgenticChatErrorBoundary } from './AgenticChatErrorBoundary';
import { AgenticChatErrorFallback } from './AgenticChatFallbacks';
import AgenticChatShadowWrapper from './AgenticChatShadowWrapper';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useAppContext } from '@/context/AppContext/AppContext';
import { useRemotionPlayerPause } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerPause';
import { showSuccessToast, showErrorToast } from '@/libs/toast/toast';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { isLayoutsWithVisionDataEnabled } from '@/stores/clip';
import { cventIconsMap, cventIconsAltIcons } from '@/components/atoms/iconConstants';

/**
 * Microfrontend loader for agentic-chat.
 *
 * Handles:
 * - Context building (AI_EDITS configuration)
 * - Module Federation infrastructure (error handling)
 * - Error fallback UI
 *
 * Note: Adapter is created in ClipsContext via useAIEditsAdapter hook.
 * Module, CSS, and data are prefetched via adapter.initialize() for instant loading.
 */
export function AgenticChatLoader({ onClose }: { onClose: () => void }) {
  const { clipId, clipData, aiEditsAdapter } = useClipsContext();
  const { play } = useRemotionPlayerPause();
  const { theme } = useAppContext();

  // Loaded dynamically so the static import graph never pulls agentic_chat/iconNames
  // into chunks that load when agentic-chat is not running.
  const [agenticIconNames, setAgenticIconNames] = useState<string[]>([]);
  useEffect(() => {
    import('agentic_chat/iconNames').then(m => setAgenticIconNames(m.AGENTIC_CHAT_ICON_NAMES)).catch(() => {});
  }, []);

  // Only the icons agentic-chat renders (agenticIconNames) get forwarded;
  // add a name there when a new cvent-themed icon is needed on that side.
  const iconOverrides = useMemo(() => {
    if (theme !== 'cvent') {
      return undefined;
    }
    const cventIcons: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
      ...cventIconsAltIcons,
      ...cventIconsMap
    };
    return agenticIconNames.reduce<Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>>((acc, name) => {
      if (cventIcons[name]) {
        acc[name] = cventIcons[name];
      }
      return acc;
    }, {});
  }, [theme, agenticIconNames]);

  const shouldShowLayoutWarning = useMemo(() => {
    if (doesClipWorkOnAlignedStreams(clipData) || isLayoutsWithVisionDataEnabled(clipData)) {
      // Aligned stream or facial rec
      return false;
    }
    return true;
  }, [clipData]);

  const context: AgenticContext = useMemo(
    () => ({
      type: 'AI_EDITS',
      metadata: { clipId },
      features: {
        recipes: true,
        helpActions: true,
        suggestions: true,
        streaming: true,
        messageOptions: true,
        disableLayoutAndZoomSuggestions: shouldShowLayoutWarning
      },
      display: {
        title: 'AI Edits',
        placeholder: 'What do you want to do?',
        emptyStateMessage: 'Start a conversation to get AI-powered suggestions.',
        warningMessage: shouldShowLayoutWarning
          ? 'No faces detected. AI-suggested zooms and scene changes are unavailable for this recording.'
          : undefined
      },
      toast: {
        success: showSuccessToast,
        error: showErrorToast
      },
      callbacks: {
        onClose,
        onPreview: play
      },
      eventBus: {
        eventNames: {
          ClearAiChat: CustomEvents.ClearAiChat,
          AIAgentStatusUpdated: CustomEvents.AIAgentStatusUpdated
        },
        on: EventBus.on,
        off: EventBus.off,
        dispatch: EventBus.dispatch
      }
    }),
    [clipId, onClose, play, shouldShowLayoutWarning]
  );

  // Don't render if adapter is not available
  if (!aiEditsAdapter) {
    return null;
  }

  return (
    <div
      className="flex size-full shrink-0 flex-col overflow-hidden rounded-[16px] bg-white"
      style={{ boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.08)' }}
    >
      <div className="flex h-full flex-col overflow-hidden">
        <AgenticChatErrorBoundary fallback={<AgenticChatErrorFallback context={context.type} onClose={onClose} />}>
          <AgenticChatShadowWrapper
            adapter={aiEditsAdapter}
            context={context}
            theme={theme}
            iconOverrides={iconOverrides}
          />
        </AgenticChatErrorBoundary>
      </div>
    </div>
  );
}

export default AgenticChatLoader;
